import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../constants';
import { CountsData, DateRange } from '../types';

interface UseCountsDataReturn {
  loading: boolean;
  qboTotals: CountsData | null;
  eservTotals: CountsData | null;
  diffs: any | null;
  handleGetCounts: () => Promise<void>;
}

const fetchQboTotals = async (): Promise<CountsData> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/qbo-totals`
    );
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to fetch QBO totals');
    }
  } catch (error) {
    console.error('Failed to fetch QBO totals:', error);
    // Return placeholder data if the endpoint fails
    return {
      totalInvoices: 0,
      totalPayments: 0,
      totalCustomers: 0,
      error: error.message
    };
  }
};

export const useCountsData = (dateRange: DateRange): UseCountsDataReturn => {
  const [loading, setLoading] = useState(false);
  const [qboTotals, setQboTotals] = useState<CountsData | null>(null);
  const [eservTotals, setEservTotals] = useState<CountsData | null>(null);
  const [diffs, setDiffs] = useState<any | null>(null);

  const fetchCounts = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      
      // Build query parameters for date range
      const queryParams = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      
      // Fetch counts from the new dev endpoint that includes both EServ and QBO data
      const devResponse = await axios.get(`${process.env.REACT_APP_SERVER_URL}${API_ENDPOINTS.DEV_COUNTS}?${queryParams}`);
      
      if (devResponse.data.success) {
        setEservTotals(devResponse.data.data.eserv);
        setQboTotals(devResponse.data.data.qbo);
        setDiffs(devResponse.data.data.diffs);
      } else {
        throw new Error(devResponse.data.message || 'Failed to fetch counts');
      }
    } catch (error) {
      console.error('Failed to fetch totals:', error);
      setQboTotals({ error: error.message });
      setEservTotals({ error: error.message });
      setDiffs(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  // Auto-fetch counts when date range changes
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const handleGetCounts = async (): Promise<void> => {
    await fetchCounts();
  };

  return {
    loading,
    qboTotals,
    eservTotals,
    diffs,
    handleGetCounts
  };
};
